/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.audio_bridge

import io.peekandpoke.klang.common.infra.KlangMap


/**
 * Defines a voice
 */
data class VoiceData(
    // note, freq
    val note: String?,
    val freqHz: Double?,

    // Gain / Dynamics
    /**
     * The channel fader: the tone-neutral level at which the voice leaves, applied with [pan] in
     * the send stage. `null` is unset and reads as 1.0.
     *
     * The ONE level word on the wire. A frontend's articulation shorthand (sprudel's `velocity`,
     * a MIDI key velocity) is multiplied into it before it crosses, so the backend never learns
     * that word (signal-flow plan section 6).
     */
    val gain: Double?,
    val legato: Double?,

    // Sound, bank, sound index
    /** Sample bank (e.g. "MPC60" or "AkaiMPC60"), optional.*/
    val bank: String?,
    /** Parsed from osc if it looks like "bd:2". sound="bd", soundIndex=2 */
    val sound: String?,
    /** Sound index */
    val soundIndex: Int?,

    // Voice slots: classic()'s door slots (with the flat "onepole" [Hz]; sprudel's `penv` as `penv.*` / `penvCurves.*`, `vib` as `vibrato.*`, the flat "accelerate", `fm` as `fm.*`), the sample's "begin"/"end"/"speed"/"loop",
    // the oscillators' own ("density", "voices", "spread") and raw ignp() writes. sprudel's "panSpread" is unread.
    val ignitorParams: KlangMap<Double>?,

    /**
     * The orbit bus slots this voice writes, keyed `<stage>.<knob>` exactly as [KatalystDsl.classic]
     * names them (`"reverb.size"`, `"compressor.ratio"`, `"duck.orbit"`). The [ignitorParams] shape, the
     * other host: `ignitorParams` is the voice's own instrument, this is the orbit's chain.
     *
     * **Applied by the orbit's OWNER voice** (the orbit's bus settings are owned by the newest `Sounding` voice; a
     * voice gives the orbit up when its gate closes or it is cut): a declared
     * chain's [IgnitorDsl.Param] knobs resolve to `katalystParams[name]` and fall back to the
     * slot's authored default when the map does not carry it. The chain RE-READS the map when its
     * instance changes, not every block; per block it only re-writes the numbers it already
     * resolved. So this is orbit state with the owner's lifetime, not a per-note snapshot: last
     * writer within the owner wins, and the values die with the voice that carried them. A knob
     * written as a constant in the chain is not a slot and is never overridden.
     *
     * Written by `.katp(name, value)` and by the bus doors as aliases (`reverb(...)`, `delay(...)`,
     * `compressor(...)`, `duck(...)`, `phaser(...)`, `body(...)`, `vowel(...)`), which is what
     * makes a door and its slot the same knob.
     *
     * Which chain reads which slot of this map is ONE rule with ONE home, the `katalystParam` door's KDoc
     * in `sprudel/lang/lang_katalyst.kt`. In short: EVERY chain reads it, for every stage it
     * declares, the chain a cylinder is born with included (Katalyst step 5b-1). It is the one
     * source of an orbit stage's knobs: the delay, reverb, compressor and duck fields left the wire
     * in step 5b-3, the phaser fields and the `filters` list that carried the vowel and the body in
     * phase 3 step 9.
     */
    val katalystParams: KlangMap<Double>? = null,

    // Routing
    val cylinder: Int?,

    // Panning (0.0 = Left, 0.5 = Center, 1.0 = Right; the engine defaults an unset pan to 0.5)
    val pan: Double?,

    // Choke group: a new voice in the same cut group ends the ones still sounding.
    val cut: Int?,

    // Solo
    /**
     * Solo amount: 0.0 = no solo, 1.0 = full solo. While a source is soloed every other voice of the playback
     * plays at `1 - amount` (the strongest solo wins), so 1.0 silences them. Recorded by the scheduler from any
     * event that carries it with a [sourceId], a [control] event included; a source's newest event wins at once.
     */
    val solo: Double?,

    /** Unique source ID for tracking which audio source this voice came from (e.g., pattern, track, instrument) */
    val sourceId: String?,

    /**
     * Output-chain name: selects the [KatalystDsl] applied to this playback's bus (its master) from
     * this event's start time onward (last writer wins per playback). The name lives in the one
     * Katalyst namespace, the same registry an orbit's [katalyst] resolves against.
     *
     * Resolved from the authoring-layer `KatalystValue` at the wire boundary: an inline chain
     * denormalizes to its `KatalystDsl.uniqueId()`, a named reference passes through. Null means
     * "no change": the playback keeps whatever master it already had.
     *
     * A master reference rides *any* event, so `note("c3").master(…)` swaps the master at that
     * note's onset and still sounds the note. An event that carries *only* a master is marked
     * [control].
     */
    val master: String? = null,

    /**
     * Orbit-chain name: selects the `KatalystDsl` the voice's orbit runs, from this event's start
     * time onward (last writer wins per orbit).
     *
     * Resolved from the authoring-layer `KatalystValue` at the wire boundary: an inline chain
     * denormalizes to its `KatalystDsl.uniqueId()`, a named reference passes through. Null means
     * "no change": the orbit keeps whatever chain it already had.
     *
     * A Katalyst reference rides *any* event, so `note("c3").katalyst(…)` swaps the orbit's chain
     * at that note's onset and still sounds the note. An event that carries *only* a Katalyst is
     * marked [control].
     *
     * The backend registers the chain but does not read it yet (Katalyst step 1, 2026-09-17); the
     * cylinder starts running declared chains in step 2.
     */
    val katalyst: String? = null,

    /**
     * Control-only event: carries engine state (a [master] or [katalyst] swap, a [solo] keep-alive) and is
     * **never synthesized**.
     *
     * The scheduler consumes such an event at its start time and drops it before voice creation.
     * The flag has to be explicit: a voice with `sound == null` is *not* silent — the ignitor
     * registry resolves a null sound to the default oscillator.
     */
    val control: Boolean? = null,

    /**
     * Semantic tags accumulated via the pattern language's `.tag(...)`. A set: tags are unique and
     * carry NO ordering guarantee. Consumed by UI subscribers (visualizations) and analysis tools;
     * the synthesis engine ignores them.
     */
    val tags: Set<String>? = null,

    /**
     * Silence-culling window in seconds (the `cull(seconds)` door). Once the voice is in its
     * release and its output has stayed under the audibility floor for this long, it ends itself
     * instead of rendering the rest of its scheduled tail. `null` = the engine default
     * ([io.peekandpoke.klang.audio_bridge.constants.VOICE_CULL_SECONDS]); a negative value
     * (`noCull()`, [io.peekandpoke.klang.audio_bridge.constants.VOICE_CULL_NEVER]) never culls.
     * The gate (the held part of the note) is never culled, whatever the value.
     */
    val cull: Double? = null,
) {
    companion object {
        val empty = VoiceData(
            note = null,
            freqHz = null,
            gain = null,
            legato = null,
            bank = null,
            sound = null,
            soundIndex = null,
            ignitorParams = null,
            katalystParams = null,
            cylinder = null,
            pan = null,
            cut = null,
            solo = null,
            sourceId = null,
        )
    }

    fun asSampleRequest(): SampleRequest {
        return SampleRequest(bank = bank, sound = sound, index = soundIndex, note = note)
    }
}
